import {
  ChakraBox,
  ChakraFlex,
  ChakraHStack,
  FAIconName,
  FireflyButton,
  ModalV2,
  TextInputV2,
} from '@equip.health/ui';
import { useEffect, useMemo, useState } from 'react';
import { ProblemTypeCode } from '~/lib/types/carePlan/ProblemTypeCode';
import { RefCarePlanProblemResponse } from '~/lib/types/carePlan/RefCarePlanProblemResponse';
import { CarePlanStatus } from '~/lib/types/carePlan/CarePlanStatus';
import { useGetRefCarePlanProblemsEnumQuery } from '~/store/services/refCarePlanEnums';
import { useGetPatientDiagnosesQuery } from '~/store/services/diagnoses';
import { useGetCarePlansByTreatmentEpisodeIdQuery } from '~/store/services/carePlan';
import { useFocusInputOnMount } from '~/lib/hooks/useFocusInputOnMount';
import Loading from '~/pages/loading';
import { AddMultiProblemSelectedRow } from '~/components/maud/patients/profile/tabs/careplan/AddMultiProblemSelectedRow';

const TOP_LEVEL_ID = 'add-multi-problem-modal';
const SEARCH_INPUT_ID = `${TOP_LEVEL_ID}-search-input`;
const SEARCH_INPUT_DOM_ID = `${SEARCH_INPUT_ID}__text-input-v2__input`;
const SEARCH_RESET_ICON_DOM_ID = `${SEARCH_INPUT_ID}__text-input-v2__reset-icon`;

// Modal grows toward 900px on tall viewports but never exceeds the space Chakra reserves for its
// own ModalContent inset. Applied to ModalV2's height prop so the OUTER modal is sized;  the body then
// uses `bodyOverflow="hidden"` so only the inner problems list scrolls.
const MODAL_HEIGHT = 'min(900px, calc(100vh - 7.5rem))';

// Ordered positions in the modal's tab cycle. The Tab handler looks up the DOM element at each position by a
// stable id. Because the modal manages focus manually and 2 stops are dynamic the handler decides skippability
// from the DOM at event time rather than from React state.
const TAB_ORDER_SEARCH = 1;
const TAB_ORDER_SEARCH_CLEAR_X_ICON = 2; // Only in tab order when the user has entered search text
const TAB_ORDER_ADD_PROBLEMS_BTN = 3; // only in tab order when at least one problem is selected
const TAB_ORDER_CANCEL_BTN = 4;
const TAB_ORDER_CLOSE_DIALOG_X_ICON = 5;
const MAX_TAB_ORDER = TAB_ORDER_CLOSE_DIALOG_X_ICON;

// Nearest dialog ancestor of an element.  The combined selector stops at whichever ancestor is closest.
const dialogRootOf = (el: Element | null): Element | null =>
  el?.closest('[aria-modal="true"], [role="dialog"]') ?? null;

const PROBLEM_TYPE_MODAL_TITLES: Record<ProblemTypeCode, string> = {
  EATING_DISORDER: 'Add ED problem',
  MEDICAL: 'Add medical problem',
  PSYCHIATRIC: 'Add psychiatric problem',
};

export type AddMultiProblemModalProps = {
  onClose: () => void;
  onSubmit: () => void;
  onToggleProblem: (problem: RefCarePlanProblemResponse) => void;
  patientId: string;
  problemTypeCode: ProblemTypeCode;
  selectedProblemsByCode: Record<string, RefCarePlanProblemResponse>;
  treatmentEpisodeId: string;
};

export const AddMultiProblemModal = ({
  onClose,
  onSubmit,
  onToggleProblem,
  patientId,
  problemTypeCode,
  selectedProblemsByCode,
  treatmentEpisodeId,
}: AddMultiProblemModalProps) => {
  const [searchText, setSearchText] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(0);

  const isMedical = problemTypeCode === ProblemTypeCode.MEDICAL;

  // ------------------- CARE PLAN(s)SETUP -------------------
  const { data: carePlans } =
    useGetCarePlansByTreatmentEpisodeIdQuery(treatmentEpisodeId);

  const activeCarePlan = carePlans?.find(
    (carePlan) => carePlan.status.code === CarePlanStatus.ACTIVE,
  );

  // ------------------- PROBLEM(s)SETUP -------------------
  const existingProblemCodes =
    activeCarePlan?.problems.map((problem) => problem.pathwayProblem.code) ||
    [];

  const existingProblemCodesSet = new Set(existingProblemCodes);

  const { data: problems, isLoading: isGetRefProblemsLoading } =
    useGetRefCarePlanProblemsEnumQuery({});

  const { data: patientDiagnoses, isLoading: isGetPatientDiagnosesLoading } =
    useGetPatientDiagnosesQuery(patientId);

  const activeDiagnosisLabels = (patientDiagnoses || [])
    .filter((diagnosis) => diagnosis.active)
    .map((diagnosis) => diagnosis.diagnosis.code);

  const {
    data: recommendedProblems,
    isLoading: isGetRecommendedProblemsLoading,
  } = useGetRefCarePlanProblemsEnumQuery(
    { diagnosisCodeLabels: activeDiagnosisLabels },
    {
      skip: isGetPatientDiagnosesLoading || activeDiagnosisLabels.length === 0,
    },
  );

  // ------------------- FILTERING AND SORTING -------------------
  const problemMatchesSearch = (problem: RefCarePlanProblemResponse) => {
    if (!searchText) return true;

    const query = searchText.trim().toLowerCase();
    if (!query) return true;

    const labelHit = problem.label.toLowerCase().includes(query);
    const bodySystemHit = (problem.bodySystem ?? '')
      .toLowerCase()
      .includes(query);
    return labelHit || bodySystemHit;
  };

  const filteredDiagnosisProblems = useMemo(
    () =>
      (recommendedProblems || [])
        .filter((problem) => problem.problemType.code === problemTypeCode)
        .filter(problemMatchesSearch)
        .sort((p1, p2) => p1.sortOrder - p2.sortOrder),
    [recommendedProblems, problemTypeCode, searchText],
  );

  const filteredOtherProblems = useMemo(
    () =>
      (problems || [])
        .filter((problem) => problem.problemType.code === problemTypeCode)
        .filter(problemMatchesSearch)
        .filter(
          (problem) =>
            !filteredDiagnosisProblems.some(
              (diagnosisProblem) => diagnosisProblem.code === problem.code,
            ),
        )
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [problemTypeCode, problems, filteredDiagnosisProblems, searchText],
  );

  // -------------------- MEDICAL BODY SYSTEM GROUPING -------------------
  const { orderedProblems, sortedBodySystems, bodySystemGroups } =
    useMemo(() => {
      if (!isMedical) {
        const others = [...filteredOtherProblems].sort(
          (a, b) => a.sortOrder - b.sortOrder,
        );
        const filteredProblemsInDisplayOrder = [
          ...filteredDiagnosisProblems,
          ...others,
        ].filter((problem) => !existingProblemCodesSet.has(problem.code));
        return {
          orderedProblems: filteredProblemsInDisplayOrder,
          sortedBodySystems: [] as string[],
          bodySystemGroups: new Map<string, RefCarePlanProblemResponse[]>(),
        };
      }

      const bodySystemGroups = new Map<string, RefCarePlanProblemResponse[]>();
      filteredOtherProblems.forEach((problem) => {
        const bodySystem = problem.bodySystem || 'Other';
        if (!bodySystemGroups.has(bodySystem)) {
          bodySystemGroups.set(bodySystem, []);
        }
        bodySystemGroups.get(bodySystem)!.push(problem);
      });

      bodySystemGroups.forEach((problemsInGroup) => {
        problemsInGroup.sort((a, b) => a.sortOrder - b.sortOrder);
      });

      const bodySystems = Array.from(bodySystemGroups.keys()).sort((a, b) =>
        a.localeCompare(b),
      );
      const problemsInDisplayOrder: RefCarePlanProblemResponse[] = [
        ...filteredDiagnosisProblems,
        ...bodySystems.flatMap((bs) => bodySystemGroups.get(bs)!),
      ];
      const filteredProblemsInDisplayOrder = problemsInDisplayOrder.filter(
        (problem) => !existingProblemCodesSet.has(problem.code),
      );

      return {
        orderedProblems: filteredProblemsInDisplayOrder,
        sortedBodySystems: bodySystems,
        bodySystemGroups,
      };
    }, [
      isMedical,
      filteredDiagnosisProblems,
      filteredOtherProblems,
      existingProblemCodesSet,
    ]);

  const codeToOrderedIndex = useMemo(() => {
    const map = new Map<string, number>();
    orderedProblems.forEach((problem, index) => {
      map.set(problem.code, index);
    });
    return map;
  }, [orderedProblems]);

  // ------------------- SELECTION AND SUBMISSION -------------------
  const selectedProblemCodes = new Set(Object.keys(selectedProblemsByCode));
  const selectionCount = selectedProblemCodes.size;
  const submitLabel =
    selectionCount === 1 ? 'Add problem' : `Add ${selectionCount} problems`;

  const isLoading =
    isGetPatientDiagnosesLoading ||
    isGetRefProblemsLoading ||
    isGetRecommendedProblemsLoading;

  useFocusInputOnMount(
    () =>
      document.getElementById(SEARCH_INPUT_DOM_ID) as HTMLInputElement | null,
    !isLoading,
  );

  // ------------------- TAB NAVIGATION -------------------
  // Tab / Shift+Tab handler.  Looks up each stop's DOM element by its stable id at event time.  The DOM is the single source of truth:
  // skippability is decided from whether the element is in the tree and whether it's a disabled button.
  useEffect(() => {
    const wrapToInitialTab = (n: number) => {
      if (n < 1) return MAX_TAB_ORDER;
      if (n > MAX_TAB_ORDER) return 1;
      return n;
    };

    const handleTab = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      const closeIcon = document.getElementById(`${TOP_LEVEL_ID}__close-icon`);

      // Tabbing is scope to this modal.
      const ourModalRoot = dialogRootOf(closeIcon);
      const eventTarget = event.target instanceof Element ? event.target : null;
      const targetModalRoot = dialogRootOf(eventTarget);

      if (!ourModalRoot || ourModalRoot !== targetModalRoot) return;

      const elementAtTabIndex = (n: number): HTMLElement | null => {
        switch (n) {
          case TAB_ORDER_SEARCH:
            return document.getElementById(SEARCH_INPUT_DOM_ID);
          case TAB_ORDER_SEARCH_CLEAR_X_ICON:
            return document.getElementById(SEARCH_RESET_ICON_DOM_ID);
          case TAB_ORDER_ADD_PROBLEMS_BTN:
            return document.getElementById(`${TOP_LEVEL_ID}-submit__button`);
          case TAB_ORDER_CANCEL_BTN:
            return document.getElementById(`${TOP_LEVEL_ID}-cancel__button`);
          case TAB_ORDER_CLOSE_DIALOG_X_ICON:
            return closeIcon;
          default:
            return null;
        }
      };

      // Reverse lookup: which position does the currently-focused element correspond to?
      // Returns null for off-cycle focus (a problem row, <body> after a focus-lock nudge, etc.);
      // caller applies a direction-aware fallback so the row's implicit place — somewhere between the search
      // field and the Cancel button — is reflected regardless of which direction the user is tabbing.
      const tabIndexForElement = (el: Element | null): number | null => {
        if (!el) return null;
        if (el.id === SEARCH_INPUT_DOM_ID) return TAB_ORDER_SEARCH;
        if (el.id === SEARCH_RESET_ICON_DOM_ID)
          return TAB_ORDER_SEARCH_CLEAR_X_ICON;
        if (el.id === `${TOP_LEVEL_ID}-submit__button`)
          return TAB_ORDER_ADD_PROBLEMS_BTN;
        if (el.id === `${TOP_LEVEL_ID}-cancel__button`)
          return TAB_ORDER_CANCEL_BTN;
        if (el.id === `${TOP_LEVEL_ID}__close-icon`)
          return TAB_ORDER_CLOSE_DIALOG_X_ICON;
        return null;
      };

      // A stop is skipped when its element isn't in the DOM (search X while search is empty) or when it's
      // a disabled button (Add while nothing is selected).
      const isTabIndexSkippable = (n: number) => {
        const el = elementAtTabIndex(n);
        if (!el) return true;
        if (el instanceof HTMLButtonElement && el.disabled) return true;
        return false;
      };

      const isAdvanceForward = !event.shiftKey;
      const current = tabIndexForElement(document.activeElement);
      // Off-cycle focus (typically a clicked problem row): treat as living somewhere between the search field
      // and the cancel button, dependent on whether the search field's reset X is display and/or if the Add button is enabled.
      const calculatedTabIndex =
        current ??
        (isAdvanceForward
          ? TAB_ORDER_SEARCH_CLEAR_X_ICON
          : TAB_ORDER_ADD_PROBLEMS_BTN);
      let nextTabIndex = wrapToInitialTab(
        isAdvanceForward ? calculatedTabIndex + 1 : calculatedTabIndex - 1,
      );
      for (
        let guard = 0;
        guard < MAX_TAB_ORDER && isTabIndexSkippable(nextTabIndex);
        guard++
      ) {
        nextTabIndex = wrapToInitialTab(
          isAdvanceForward ? nextTabIndex + 1 : nextTabIndex - 1,
        );
      }

      const target = elementAtTabIndex(nextTabIndex);
      if (target) {
        event.preventDefault();
        event.stopImmediatePropagation();
        target.focus();
      }
    };

    window.addEventListener('keydown', handleTab, true);
    return () => window.removeEventListener('keydown', handleTab, true);
  }, []);

  // ------------------- KEYBOARD NAVIGATION -------------------
  // Scroll highlighted problem into view when the up/down arrows change highlighted problem.
  useEffect(() => {
    const focusedProblem = orderedProblems[focusedIndex];
    if (!focusedProblem) return;
    const element = document.getElementById(
      `${TOP_LEVEL_ID}-row-${focusedProblem.code}`,
    );
    if (element && typeof element.scrollIntoView === 'function') {
      element.scrollIntoView({ block: 'nearest' });
    }
  }, [focusedIndex, orderedProblems]);

  useEffect(() => {
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const isSearchInputTarget = target?.id === SEARCH_INPUT_DOM_ID;

      if (event.key === 'ArrowDown') {
        if (orderedProblems.length === 0) return;
        event.preventDefault();
        setFocusedIndex((idx) => Math.min(idx + 1, orderedProblems.length - 1));
      } else if (event.key === 'ArrowUp') {
        if (orderedProblems.length === 0) return;
        event.preventDefault();
        setFocusedIndex((idx) => Math.max(idx - 1, 0));
      } else if (event.key === 'Escape' && isSearchInputTarget && searchText) {
        event.preventDefault();
        event.stopPropagation();
        setSearchText('');
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [orderedProblems, searchText]);

  // ------------- Enter and Space Bar MANAGEMENT ----------------
  // Modular Enter / Space handler, scoped to this modal via the same shared-aria-modal check the Tab handler uses.
  //
  // Rules (Enter and Space):
  //   * On a <button> or [role="button"]: let native activation fire the control's action.
  //   * Space on the search <input>: let native typing insert a space character.
  //   * Anywhere else: toggle the currently-highlighted problem row.
  useEffect(() => {
    const handleActivationKeys = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;

      // Scope to this modal so nested overlays rendered outside our DOM subtree keep native handling.
      const closeIcon = document.getElementById(`${TOP_LEVEL_ID}__close-icon`);
      const ourModalRoot = dialogRootOf(closeIcon);
      const eventTarget = event.target instanceof Element ? event.target : null;
      const targetModalRoot = dialogRootOf(eventTarget);
      if (!ourModalRoot || ourModalRoot !== targetModalRoot) return;

      const isButtonTarget =
        eventTarget instanceof HTMLButtonElement ||
        eventTarget?.getAttribute('role') === 'button';
      if (isButtonTarget) return;

      if (
        event.key === ' ' &&
        eventTarget instanceof HTMLElement &&
        eventTarget.id === SEARCH_INPUT_DOM_ID
      ) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      const problem = orderedProblems[focusedIndex];
      if (problem) {
        onToggleProblem(problem);
      }
    };

    window.addEventListener('keydown', handleActivationKeys, true);
    return () =>
      window.removeEventListener('keydown', handleActivationKeys, true);
  }, [focusedIndex, orderedProblems, onToggleProblem]);

  return (
    <ModalV2
      footerContent={
        <ChakraHStack
          data-testid={`${TOP_LEVEL_ID}-footer`}
          justifyContent="space-between"
          paddingTop="14px"
          width="100%"
        >
          <FireflyButton
            id={`${TOP_LEVEL_ID}-cancel`}
            leftIcon={FAIconName.xmarkLight}
            onClick={onClose}
            variant="secondary"
          >
            Cancel
          </FireflyButton>
          <FireflyButton
            id={`${TOP_LEVEL_ID}-submit`}
            isDisabled={selectionCount === 0}
            onClick={onSubmit}
            rightIcon={FAIconName.chevronRightRegular}
          >
            {submitLabel}
          </FireflyButton>
        </ChakraHStack>
      }
      bodyOverflow="hidden"
      height={MODAL_HEIGHT}
      id={TOP_LEVEL_ID}
      isOpen
      onClose={onClose}
      size="3xl"
      title={PROBLEM_TYPE_MODAL_TITLES[problemTypeCode]}
    >
      {isLoading ? (
        <Loading />
      ) : (
        <ChakraFlex
          data-testid={`${TOP_LEVEL_ID}-body`}
          flexDirection="column"
          height="100%"
        >
          <ChakraBox
            data-testid={`${TOP_LEVEL_ID}-search-wrapper`}
            marginBottom="24px"
          >
            <TextInputV2
              id={SEARCH_INPUT_ID}
              isReset
              leftIcon={FAIconName.magnifyingGlassRegular}
              onChange={(newText) => {
                setSearchText(newText);
                setFocusedIndex(0);
                if (newText === '') {
                  document.getElementById(SEARCH_INPUT_DOM_ID)?.focus();
                }
              }}
              placeholder={
                isMedical
                  ? 'Search by problem name or body system'
                  : 'Search problems'
              }
              value={searchText}
            />
          </ChakraBox>
          <ChakraFlex
            data-testid={`${TOP_LEVEL_ID}-problems-list`}
            flex="1"
            flexDirection="column"
            gap="8px"
            overflowY="auto"
            tabIndex={-1}
          >
            {filteredDiagnosisProblems.map((problem) => {
              const isExisting = existingProblemCodesSet.has(problem.code);
              const orderedIndex = codeToOrderedIndex.get(problem.code);
              return (
                <AddMultiProblemSelectedRow
                  idPrefix={TOP_LEVEL_ID}
                  isChecked={selectedProblemCodes.has(problem.code)}
                  isExistingInCarePlan={isExisting}
                  isFocused={!isExisting && orderedIndex === focusedIndex}
                  isRecommended
                  key={problem.code}
                  onMouseEnter={
                    orderedIndex !== undefined
                      ? () => setFocusedIndex(orderedIndex)
                      : undefined
                  }
                  onToggle={() => {
                    if (!isExisting) onToggleProblem(problem);
                  }}
                  problem={problem}
                  showBodySystem={isMedical}
                />
              );
            })}
            {isMedical
              ? sortedBodySystems.map((bs) =>
                  bodySystemGroups.get(bs)!.map((problem) => {
                    const isExisting = existingProblemCodesSet.has(
                      problem.code,
                    );
                    const orderedIndex = codeToOrderedIndex.get(problem.code);
                    return (
                      <AddMultiProblemSelectedRow
                        idPrefix={TOP_LEVEL_ID}
                        isChecked={selectedProblemCodes.has(problem.code)}
                        isExistingInCarePlan={isExisting}
                        isFocused={!isExisting && orderedIndex === focusedIndex}
                        key={problem.code}
                        onMouseEnter={
                          orderedIndex !== undefined
                            ? () => setFocusedIndex(orderedIndex)
                            : undefined
                        }
                        onToggle={() => {
                          if (!isExisting) onToggleProblem(problem);
                        }}
                        problem={problem}
                        showBodySystem
                      />
                    );
                  }),
                )
              : filteredOtherProblems.map((problem) => {
                  const isExisting = existingProblemCodesSet.has(problem.code);
                  const orderedIndex = codeToOrderedIndex.get(problem.code);
                  return (
                    <AddMultiProblemSelectedRow
                      idPrefix={TOP_LEVEL_ID}
                      isChecked={selectedProblemCodes.has(problem.code)}
                      isExistingInCarePlan={isExisting}
                      isFocused={!isExisting && orderedIndex === focusedIndex}
                      key={problem.code}
                      onMouseEnter={
                        orderedIndex !== undefined
                          ? () => setFocusedIndex(orderedIndex)
                          : undefined
                      }
                      onToggle={() => {
                        if (!isExisting) onToggleProblem(problem);
                      }}
                      problem={problem}
                      showBodySystem={false}
                    />
                  );
                })}
          </ChakraFlex>
        </ChakraFlex>
      )}
    </ModalV2>
  );
};
